<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import SidebarOrgZone from '@/components/layout/sidebar/SidebarOrgZone.vue'
import SidebarEventZone from '@/components/layout/sidebar/SidebarEventZone.vue'
import SidebarEditionZone from '@/components/layout/sidebar/SidebarEditionZone.vue'
import SidebarRaceZone from '@/components/layout/sidebar/SidebarRaceZone.vue'

const props = defineProps<{
  mobileOpen: boolean
}>()

const emit = defineEmits<{ (e: 'close'): void }>()

const route = useRoute()

type SidebarZone = 'organisation' | 'event' | 'edition' | 'race'

const zone = computed((): SidebarZone => {
  if (route.params['raceSlug']) return 'race'
  if (route.params['editionSlug']) return 'edition'
  if (route.params['eventSlug']) return 'event'
  return 'organisation'
})

const eventSlug = computed((): string | null =>
  route.params['eventSlug'] ? String(route.params['eventSlug']) : null,
)
const editionSlug = computed((): string | null =>
  route.params['editionSlug'] ? String(route.params['editionSlug']) : null,
)
const raceSlug = computed((): string | null =>
  route.params['raceSlug'] ? String(route.params['raceSlug']) : null,
)
</script>

<template>
  <!-- Desktop: always visible, sticky below top bar -->
  <aside
    class="hidden lg:flex flex-col w-64 shrink-0 border-r border-slate-800 bg-slate-900 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto"
    aria-label="Organizer navigation"
  >
    <SidebarOrgZone v-if="zone === 'organisation'" />
    <SidebarEventZone v-else-if="zone === 'event'" :event-slug="eventSlug!" />
    <SidebarRaceZone v-else-if="zone === 'race'" :event-slug="eventSlug!" :edition-slug="editionSlug!" :race-slug="raceSlug!" />
    <SidebarEditionZone v-else :event-slug="eventSlug!" :edition-slug="editionSlug!" />
  </aside>

  <!-- Mobile: off-canvas drawer -->
  <Transition
    enter-active-class="transition-transform duration-200 ease-out"
    enter-from-class="-translate-x-full"
    enter-to-class="translate-x-0"
    leave-active-class="transition-transform duration-200 ease-in"
    leave-from-class="translate-x-0"
    leave-to-class="-translate-x-full"
  >
    <aside
      v-if="props.mobileOpen"
      class="lg:hidden fixed inset-y-0 left-0 z-30 flex flex-col w-64 bg-slate-900 border-r border-slate-800 overflow-y-auto"
      aria-label="Organizer navigation"
    >
      <div class="flex items-center justify-between px-4 h-16 border-b border-slate-800 shrink-0">
        <span class="text-sm font-semibold text-white">Menu</span>
        <button
          type="button"
          class="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          @click="emit('close')"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <SidebarOrgZone v-if="zone === 'organisation'" @navigate="emit('close')" />
      <SidebarEventZone v-else-if="zone === 'event'" :event-slug="eventSlug!" @navigate="emit('close')" />
      <SidebarRaceZone v-else-if="zone === 'race'" :event-slug="eventSlug!" :edition-slug="editionSlug!" :race-slug="raceSlug!" @navigate="emit('close')" />
      <SidebarEditionZone v-else :event-slug="eventSlug!" :edition-slug="editionSlug!" @navigate="emit('close')" />
    </aside>
  </Transition>
</template>
